/** Manages all save data across three tiers: meta progression, per-character progression, and per-run state. */
import type { Position, V4TreeProgress } from '../utils/Types';
import { StorageBackend, LocalStorageBackend } from './StorageBackend';

/** Volatile in-run state cleared on death: health, position, zone, and carried inventory. */
export interface RunState {
  hp: number;
  maxHp: number;
  position: Position;
  zone: string;
  inventory: string[];
}

/** Per-character data that survives death: skill trees, attunement slots, tree selection, unlocked skills, and discovered lore. */
export interface CharacterProgression {
  skillProgress: Record<string, V4TreeProgress>;
  attunedSkills: string[];
  attunementCapacity: number;
  selectedTrees: string[];
  unlockedSkills: string[];
  discoveredLore: string[];
  /** V4 hotbar: exact 4-slot array preserving slot positions. Absent in old saves. */
  hotbar?: (string | null)[];
}

/** Global persistent stats and settings shared across all characters and runs. */
export interface MetaProgression {
  totalRuns: number;
  totalDeaths: number;
  settings: GameSettings;
}

/** Player-configurable options stored inside MetaProgression: audio levels, screen shake, and key bindings. */
export interface GameSettings {
  musicVolume: number;
  sfxVolume: number;
  screenShake: boolean;
  eventLogEnabled: boolean;
  keyBindings?: Record<string, { primary: number; alternate?: number }>;
}

const DEFAULT_SETTINGS: GameSettings = {
  musicVolume: 0.7,
  sfxVolume: 1.0,
  screenShake: true,
  eventLogEnabled: true,
};

const DEFAULT_META: MetaProgression = {
  totalRuns: 0,
  totalDeaths: 0,
  settings: DEFAULT_SETTINGS,
};

const DEFAULT_CHARACTER: CharacterProgression = {
  skillProgress: {},
  attunedSkills: [],
  attunementCapacity: 1,
  selectedTrees: [],
  unlockedSkills: [],
  discoveredLore: [],
};

/**
 * Save format version tag. On construction, SaveSystem compares this against
 * the value stored in localStorage. A mismatch wipes all save data and writes
 * the new version. Debug config (`debug-config` key) is unaffected.
 *
 * **Bump this whenever you change the shape of saved data:**
 * RunState, CharacterProgression, MetaProgression, GameSettings,
 * or anything that alters how those interfaces are serialized/deserialized.
 */
const SAVE_VERSION = 2;
const VERSION_KEY = 'save-version';

/** Reads and writes all game save data through an injectable StorageBackend. */
export class SaveSystem {
  private backend: StorageBackend;

  constructor(backend?: StorageBackend) {
    this.backend = backend ?? new LocalStorageBackend();
    this.migrateIfNeeded();
  }

  private migrateIfNeeded(): void {
    const stored = this.backend.read(VERSION_KEY);
    const current = String(SAVE_VERSION);
    if (stored === current) return;

    // No stored version = first visit or pre-versioning install. Just stamp it.
    // Actual mismatch = incompatible save format. Nuke everything except debug config.
    if (stored !== null) {
      const saves = this.backend.list('run');
      console.warn(
        `[SaveSystem] Version mismatch: stored v${stored} → current v${current}. ` +
        `Wiping all save data (${saves.length} save slot(s): ${saves.length ? saves.join(', ') : 'none'}). ` +
        `Debug config preserved.`,
      );
      this.backend.deleteAll(['debug-config']);
    } else {
      console.log(`[SaveSystem] No stored version found (first visit or pre-versioning install). Stamping v${current}.`);
    }
    this.backend.write(VERSION_KEY, current);
  }

  // --- Run State (keyed by save name) ---

  /** Persist the in-progress run state for the named save slot. */
  saveRun(name: string, state: RunState): void {
    this.backend.write(`run:${name}`, JSON.stringify(state));
  }

  /** Load the saved run state for the named slot, or null if none exists. */
  loadRun(name: string): RunState | null {
    const data = this.backend.read(`run:${name}`);
    if (!data) return null;

    try {
      return JSON.parse(data) as RunState;
    } catch {
      console.error(`[SaveSystem] Corrupt run state for slot "${name}" — JSON parse failed. Raw data: ${data.slice(0, 200)}${data.length > 200 ? '…' : ''}. Clearing slot.`);
      this.backend.delete(`run:${name}`);
      return null;
    }
  }

  /** Delete the run state for the named slot. */
  clearRun(name: string): void {
    console.log(`[SaveSystem] Clearing run state for slot "${name}" (death or explicit clear).`);
    this.backend.delete(`run:${name}`);
  }

  /** Return true if a run state exists for the named slot. */
  hasRun(name: string): boolean {
    return this.backend.has(`run:${name}`);
  }

  /** Return the names of all existing run save slots. */
  listSaves(): string[] {
    return this.backend.list('run');
  }

  /** Delete both the run state and character progression for the named slot. */
  deleteSave(name: string): void {
    console.log(`[SaveSystem] Deleting full save for slot "${name}" (run state + character progression).`);
    this.backend.delete(`run:${name}`);
    this.backend.delete(`char:${name}`);
  }

  // --- Character Progression (per-character, survives death) ---

  /** Persist per-character progression data (skill trees, attunement, lore). */
  saveCharacter(name: string, data: CharacterProgression): void {
    this.backend.write(`char:${name}`, JSON.stringify(data));
  }

  /** Load per-character progression, falling back to legacy meta migration then defaults. */
  loadCharacter(name: string): CharacterProgression {
    const data = this.backend.read(`char:${name}`);
    if (data) {
      try {
        const parsed = JSON.parse(data) as Partial<CharacterProgression>;
        return { ...DEFAULT_CHARACTER, ...parsed };
      } catch {
        console.error(`[SaveSystem] Corrupt character data for slot "${name}" — JSON parse failed. Raw data: ${data.slice(0, 200)}${data.length > 200 ? '…' : ''}. Returning defaults.`);
        return { ...DEFAULT_CHARACTER };
      }
    }

    return { ...DEFAULT_CHARACTER };
  }

  /** Delete the character progression record for the named slot. */
  deleteCharacter(name: string): void {
    console.log(`[SaveSystem] Deleting character progression for slot "${name}".`);
    this.backend.delete(`char:${name}`);
  }

  // --- Meta Progression ---

  /** Persist global meta progression (total runs, deaths, settings). */
  saveMeta(meta: MetaProgression): void {
    this.backend.write('meta', JSON.stringify(meta));
  }

  /** Load global meta progression, returning defaults if none exists or data is corrupt. */
  loadMeta(): MetaProgression {
    const data = this.backend.read('meta');
    if (!data) return { ...DEFAULT_META, settings: { ...DEFAULT_SETTINGS } };

    try {
      const parsed = JSON.parse(data) as Partial<MetaProgression>;
      return {
        ...DEFAULT_META,
        ...parsed,
        settings: { ...DEFAULT_SETTINGS, ...parsed.settings },
      };
    } catch {
      console.error(`[SaveSystem] Corrupt meta progression — JSON parse failed. Raw data: ${data.slice(0, 200)}${data.length > 200 ? '…' : ''}. Returning defaults.`);
      return { ...DEFAULT_META, settings: { ...DEFAULT_SETTINGS } };
    }
  }

  // --- Settings (convenience, stored inside meta) ---

  /** Convenience wrapper: save game settings into the meta record. */
  saveSettings(settings: GameSettings): void {
    const meta = this.loadMeta();
    meta.settings = settings;
    this.saveMeta(meta);
  }

  /** Convenience wrapper: load game settings from the meta record. */
  loadSettings(): GameSettings {
    return this.loadMeta().settings;
  }

  // --- Danger Zone ---

  /** Wipe all run states, character progressions, and meta data from storage. */
  clearAll(): void {
    const saves = this.listSaves();
    console.warn(`[SaveSystem] clearAll invoked — wiping ${saves.length} save slot(s)${saves.length ? ` (${saves.join(', ')})` : ''} + meta progression.`);
    for (const name of saves) {
      this.backend.delete(`run:${name}`);
      this.backend.delete(`char:${name}`);
    }
    this.backend.delete('meta');
  }
}
